import React, { useState, useEffect } from "react";
import axios from "axios";
import TeamDayTable from "../components/TeamDayTable";
import { C as TOKENS, FONT } from "../constants";
import PageHeader from "../components/PageHeader";
import { getApiBase } from "../apiBase";
import { formatApiError } from "../utils/apiError";
import {
  claimLocalFingerprint,
  fingerprintsFromRecords,
  isStatementTaken,
  loadLocalUsedFingerprints,
  extractProvenance,
} from "../utils/problemStatementMeta";

const BASE = getApiBase();
const authH = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});
const LS_KEY = "ps_draft";

// Align with Schedule Planner blue tokens
const C = {
  primary: TOKENS.primary,
  primaryLight: TOKENS.primarySoft,
  primary2: TOKENS.primary2,
  primaryDark: TOKENS.primaryDark,
  bg: TOKENS.bg,
  surface: TOKENS.surface,
  border: TOKENS.border,
  borderFocus: TOKENS.primary,
  error: TOKENS.error,
  errorBg: TOKENS.errorBg,
  success: TOKENS.success,
  successBg: TOKENS.successBg,
  muted: TOKENS.muted,
  text: TOKENS.text,
  accent: TOKENS.primaryDark,
  subtleText: TOKENS.muted,
  panelBg: TOKENS.surfaceAlt,
  cardHover: TOKENS.primarySoft,
  ai: TOKENS.warning,
  aiBg: TOKENS.warningBg,
};

const FIVE_PILLARS = [
  { id: "CFC", label: "Center for Creativity", emoji: "🎨", color: "#EC4899" },
  {
    id: "CLT",
    label: "Center for Learning & Teaching",
    emoji: "📖",
    color: "#0B5FFF",
  },
  {
    id: "SCD",
    label: "Skill & Career Development",
    emoji: "💼",
    color: "#0891B2",
  },
  {
    id: "IIPC",
    label: "Industry Institute Partnership",
    emoji: "🏭",
    color: "#1B4F72",
  },
  {
    id: "SRI",
    label: "Social Responsibility Initiatives",
    emoji: "🌍",
    color: "#059669",
  },
];

// DT Playbook phases
const DT_PHASES = [
  {
    id: "empathize",
    label: "Empathize & Define",
    emoji: "🔍",
    color: "#0D9488",
    desc: "User research, personas, problem & opportunity identification",
  },
  {
    id: "plan",
    label: "Plan",
    emoji: "🛠",
    color: "#65A30D",
    desc: "User actions, app state, features, inclusion, UI/UX",
  },
  {
    id: "prototype",
    label: "Prototype",
    emoji: "📐",
    color: "#06B6D4",
    desc: "Sketch screens, storyboard, design style, build",
  },
  {
    id: "evaluate",
    label: "Evaluate",
    emoji: "✅",
    color: "#EC4899",
    desc: "App pitch, user testing, observation, interview",
  },
  {
    id: "pitch",
    label: "Pitch & BMC",
    emoji: "🚀",
    color: "#8B5CF6",
    desc: "Customer sales pitch, BMC analysis, pitch deck",
  },
];

// Week milestone options
const WEEK_OPTIONS = [
  { weeks: 2, label: "Sprint", emoji: "⚡" },
  { weeks: 4, label: "Module", emoji: "📦" },
  { weeks: 8, label: "Semester", emoji: "🎓" },
  { weeks: 12, label: "Project", emoji: "🏆" },
  { weeks: 16, label: "Full Year", emoji: "🌟" },
];

const DIFF_COLORS = {
  beginner: "#059669",
  intermediate: "#1B4F72",
  advanced: "#DC2626",
};

// DT Playbook — full phase content (mirrors backend DT_PHASE_DETAILS), used by
// the DT Playbook reference panel so the actual playbook content is visible,
// not just short chip labels.
const DT_PHASE_FULL = {
  empathize: {
    goal: "Understand real users before designing anything.",
    activities: [
      "Conduct user research (interviews/surveys)",
      "Create user personas",
      "Build empathy maps",
      "Define the opportunity/problem statement",
    ],
  },
  plan: {
    goal: "Turn the problem into a concrete plan for what to build.",
    activities: [
      "Design user actions & flows",
      "Define app state & data model",
      "Select features (MoSCoW/priority)",
      "Plan for inclusion & accessibility",
      "Rough UI/UX wireframes",
    ],
  },
  prototype: {
    goal: "Make the idea tangible enough to test.",
    activities: [
      "Sketch key screens",
      "Create a storyboard of the user journey",
      "Define app behaviour/logic",
      "Establish a visual style guide",
      "Build an interactive Figma prototype",
    ],
  },
  evaluate: {
    goal: "Test with real users and learn before building further.",
    activities: [
      "Pitch the app to peers for feedback",
      "Run moderated user testing",
      "Observe usage without prompting",
      "Conduct post-testing interviews",
      "Log feedback and iterate the design",
    ],
  },
  pitch: {
    goal: "Package the work as a fundable, sellable product.",
    activities: [
      "Business Model Canvas analysis",
      "Prepare a customer-facing sales pitch",
      "Build a pitch deck for mentors/industry/alumni",
    ],
  },
};

const globalStyles = `
  * { box-sizing: border-box; }
  input, textarea, select { font-family: inherit; }
  input:focus, textarea:focus, select:focus { outline: none !important; border-color: ${TOKENS.primary} !important; box-shadow: 0 0 0 3px ${TOKENS.primary}20; }
  button:focus-visible { outline: 2px solid ${TOKENS.primary}; outline-offset: 2px; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: ${TOKENS.bg}; }
  ::-webkit-scrollbar-thumb { background: ${TOKENS.borderStrong}; border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: ${TOKENS.primary2}; }
  @keyframes shimmer { 0%{transform:translateX(-200%)} 100%{transform:translateX(300%)} }
  @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin { to { transform: rotate(360deg); } }
  .ps-card:hover { background: ${TOKENS.primarySoft} !important; }
  .ps-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px ${TOKENS.primary}40 !important; }
  .ps-btn-outline:hover { background: ${TOKENS.primarySoft} !important; }
  .ps-subject-card:hover { border-color: ${TOKENS.primary2} !important; }
  .ps-pillar-chip:hover { transform: scale(1.03); }
  .ps-week-chip:hover { transform: scale(1.03); }
  .ps-inspire-row {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 10px; border-radius: 8px;
    background: #FFFFFFB0; border: 1.5px solid #BFDBFE;
    transition: background .15s ease, border-color .15s ease, transform .15s ease, box-shadow .15s ease;
  }
  .ps-inspire-row:hover, .ps-inspire-row.is-hover {
    background: #E7F0F6; border-color: #1B4F72; transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(27,79,114,.12);
  }
  .ps-inspire-row.is-picked { background: #E6F7F5; border-color: #0F766E; box-shadow: 0 0 0 3px #0F766E22; }
  .ps-inspire-use {
    padding: 4px 10px; border-radius: 6px; border: 1.5px solid #1B4F72;
    background: #E7F0F6; color: #143B56; font-size: 11px; font-weight: 700;
    cursor: pointer; flex-shrink: 0; transition: background .15s, color .15s, border-color .15s;
  }
  .ps-inspire-use:hover { background: #1B4F72; color: #fff; }
  .ps-live-chip {
    display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
    transition: transform .15s, filter .15s;
  }
  .ps-live-chip:hover { transform: translateY(-1px); filter: brightness(1.05); }
`;

function Badge({ label, color = C.muted }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 9px",
        borderRadius: 99,
        border: `1px solid ${color}30`,
        color,
        background: color + "12",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

function PillarTag({ label }) {
  const p = FIVE_PILLARS.find(
    (p) => label.includes(p.id) || label.includes(p.label.split(" ")[0]),
  );
  const color = p ? p.color : C.muted;
  return <Badge label={label} color={color} />;
}

function FieldLabel({ children, required }) {
  return (
    <label
      style={{
        display: "block",
        fontSize: 11,
        fontWeight: 700,
        color: C.muted,
        letterSpacing: "0.07em",
        marginBottom: 6,
        textTransform: "uppercase",
      }}
    >
      {children}
      {required && <span style={{ color: C.error, marginLeft: 2 }}>*</span>}
    </label>
  );
}

function TextInput({ value, onChange, placeholder, style }) {
  return (
    <input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: "100%",
        padding: "9px 12px",
        border: `1.5px solid ${C.border}`,
        borderRadius: 8,
        fontSize: 13,
        color: C.text,
        background: C.surface,
        transition: "border-color 0.15s, box-shadow 0.15s",
        ...style,
      }}
    />
  );
}

function Divider() {
  return <div style={{ height: 1, background: C.border, margin: "20px 0" }} />;
}

// ── Multi-select Pillars ──────────────────────────────────────────────────────
function PillarMultiSelect({ selected, onChange }) {
  function toggle(id) {
    onChange(
      selected.includes(id)
        ? selected.filter((x) => x !== id)
        : [...selected, id],
    );
  }
  return (
    <div>
      <FieldLabel required>
        5 Pillars{" "}
        <span style={{ fontWeight: 400, textTransform: "none", fontSize: 10 }}>
          (select all that apply)
        </span>
      </FieldLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {FIVE_PILLARS.map((p) => {
          const active = selected.includes(p.id);
          return (
            <button
              key={p.id}
              className="ps-pillar-chip"
              onClick={() => toggle(p.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 12px",
                borderRadius: 9,
                cursor: "pointer",
                textAlign: "left",
                background: active ? p.color + "14" : C.bg,
                border: `1.5px solid ${active ? p.color : C.border}`,
                transition: "all 0.18s",
              }}
            >
              <span style={{ fontSize: 16 }}>{p.emoji}</span>
              <span
                style={{
                  flex: 1,
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  color: active ? p.color : C.muted,
                }}
              >
                {p.label}
              </span>
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 5,
                  border: `2px solid ${active ? p.color : C.border}`,
                  background: active ? p.color : C.surface,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  color: "#fff",
                  fontWeight: 900,
                  flexShrink: 0,
                  transition: "all 0.15s",
                }}
              >
                {active ? "✓" : ""}
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
        {selected.length === 0
          ? "⚠ Select at least 2 pillars"
          : `${selected.length} pillar${selected.length > 1 ? "s" : ""} selected`}
      </div>
    </div>
  );
}

// ── Multi-select Weeks (fixed, specific durations only) ─────────────────────
// WEEK_OPTIONS is a fixed, specific set — not freeform typing — so results
// are reproducible. When more than one is picked, each chunk is paired 1:1
// with a selected DT phase (same pairing logic the backend uses), so the
// preview here always matches what actually gets generated.
function WeekMultiSelect({ selected, onChange, dtPhases }) {
  function toggle(w) {
    onChange(
      selected.includes(w) ? selected.filter((x) => x !== w) : [...selected, w],
    );
  }
  const sorted = [...selected].sort((a, b) => a - b); // don't mutate state array
  const totalWeeks = sorted.reduce((a, b) => a + b, 0);
  const phaseLabels = dtPhases.map(
    (id) => DT_PHASES.find((p) => p.id === id)?.label || id,
  );

  // Mirrors backend: 1:1 pairing when counts match, else even spread.
  let mapping = [];
  if (sorted.length > 1 && sorted.length === phaseLabels.length) {
    mapping = sorted.map((w, i) => ({ weeks: w, phase: phaseLabels[i] }));
  } else if (phaseLabels.length) {
    const perPhase = Math.max(1, Math.round(totalWeeks / phaseLabels.length));
    let remaining = totalWeeks;
    mapping = phaseLabels.map((phase, i) => {
      const isLast = i === phaseLabels.length - 1;
      const w = isLast ? remaining : Math.min(perPhase, remaining);
      remaining -= w;
      return { weeks: Math.max(1, w), phase };
    });
  }

  return (
    <div>
      <FieldLabel required>
        Project Duration{" "}
        <span style={{ fontWeight: 400, textTransform: "none", fontSize: 10 }}>
          (fixed durations — pick one or more)
        </span>
      </FieldLabel>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {WEEK_OPTIONS.map((opt) => {
          const active = selected.includes(opt.weeks);
          return (
            <button
              key={opt.weeks}
              className="ps-week-chip"
              onClick={() => toggle(opt.weeks)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                padding: "10px 14px",
                borderRadius: 10,
                cursor: "pointer",
                minWidth: 72,
                background: active ? C.accent + "14" : C.bg,
                border: `1.5px solid ${active ? C.accent : C.border}`,
                transition: "all 0.18s",
              }}
            >
              <span style={{ fontSize: 18 }}>{opt.emoji}</span>
              <span
                style={{
                  fontWeight: 800,
                  fontSize: 16,
                  color: active ? C.accent : C.text,
                }}
              >
                {opt.weeks}w
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: active ? C.accent : C.muted,
                  fontWeight: 600,
                }}
              >
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
      {sorted.length > 0 && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: 8,
            background: C.accent + "10",
            border: `1px solid ${C.accent}20`,
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: C.accent,
              fontWeight: 700,
              marginBottom: mapping.length ? 6 : 0,
            }}
          >
            Selected: {sorted.map((w) => `${w}w`).join(", ")} → Total{" "}
            {totalWeeks} weeks
          </div>
          {mapping.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {mapping.map((m, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 11,
                    color: C.subtleText,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>{m.phase}</span>
                  <span style={{ fontWeight: 700 }}>{m.weeks}w</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── DT Playbook Phase Selector ────────────────────────────────────────────────
function DTPhaseSelector({
  selected,
  onChange,
  showPlaybook,
  onTogglePlaybook,
}) {
  function toggle(id) {
    onChange(
      selected.includes(id)
        ? selected.filter((x) => x !== id)
        : [...selected, id],
    );
  }
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <FieldLabel>
          DT Playbook Phases{" "}
          <span
            style={{ fontWeight: 400, textTransform: "none", fontSize: 10 }}
          >
            (structure deliverables by phase)
          </span>
        </FieldLabel>
        <button
          onClick={onTogglePlaybook}
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: C.primary,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            textDecoration: "underline",
          }}
        >
          {showPlaybook ? "Hide playbook ▲" : "View full playbook ▾"}
        </button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {DT_PHASES.map((ph) => {
          const active = selected.includes(ph.id);
          return (
            <button
              key={ph.id}
              onClick={() => toggle(ph.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 12px",
                borderRadius: 8,
                cursor: "pointer",
                background: active ? ph.color + "15" : C.bg,
                border: `1.5px solid ${active ? ph.color : C.border}`,
                transition: "all 0.18s",
              }}
            >
              <span style={{ fontSize: 14 }}>{ph.emoji}</span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  color: active ? ph.color : C.muted,
                }}
              >
                {ph.label}
              </span>
            </button>
          );
        })}
      </div>
      {showPlaybook && <DTPlaybookPanel />}
    </div>
  );
}

// ── DT Playbook full reference panel ─────────────────────────────────────────
// Shows the actual SNS DT Playbook content phase-by-phase (goal + activities),
// not just the short chip labels used for selection.
function DTPlaybookPanel() {
  const [openId, setOpenId] = useState(DT_PHASES[0].id);
  return (
    <div
      style={{
        marginTop: 10,
        borderRadius: 10,
        border: `1.5px solid ${C.border}`,
        overflow: "hidden",
      }}
    >
      {DT_PHASES.map((ph, i) => {
        const content = DT_PHASE_FULL[ph.id];
        const open = openId === ph.id;
        return (
          <div
            key={ph.id}
            style={{ borderTop: i === 0 ? "none" : `1px solid ${C.border}` }}
          >
            <button
              onClick={() => setOpenId(open ? null : ph.id)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 12px",
                background: open ? ph.color + "10" : C.surface,
                border: "none",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: ph.color + "20",
                  color: ph.color,
                  fontSize: 10,
                  fontWeight: 800,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {i + 1}
              </span>
              <span style={{ fontSize: 14 }}>{ph.emoji}</span>
              <span
                style={{
                  flex: 1,
                  fontSize: 12,
                  fontWeight: 700,
                  color: open ? ph.color : C.text,
                }}
              >
                {ph.label}
              </span>
              <span style={{ fontSize: 10, color: C.muted }}>
                {open ? "▲" : "▼"}
              </span>
            </button>
            {open && content && (
              <div
                style={{
                  padding: "4px 14px 12px 42px",
                  background: ph.color + "06",
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontStyle: "italic",
                    color: C.subtleText,
                    marginBottom: 6,
                  }}
                >
                  {content.goal}
                </div>
                {content.activities.map((a, j) => (
                  <div
                    key={j}
                    style={{
                      fontSize: 12,
                      color: C.subtleText,
                      padding: "2px 0",
                      display: "flex",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        color: ph.color,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      •
                    </span>{" "}
                    {a}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── AI Integration Toggle ─────────────────────────────────────────────────────
function AIIntegrationToggle({ value, onChange }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 14px",
        borderRadius: 10,
        background: value ? C.aiBg : C.bg,
        border: `1.5px solid ${value ? C.ai : C.border}`,
        transition: "all 0.2s",
        cursor: "pointer",
      }}
      onClick={() => onChange(!value)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 20 }}>🤖</span>
        <div>
          <div
            style={{
              fontWeight: 700,
              fontSize: 13,
              color: value ? C.ai : C.text,
            }}
          >
            GenAI Integration
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
            Include AI/ML tools, LLMs, or Generative AI in the project
          </div>
        </div>
      </div>
      <div
        style={{
          width: 44,
          height: 24,
          borderRadius: 12,
          background: value ? C.ai : C.border,
          position: "relative",
          transition: "background 0.2s",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 2,
            left: value ? 22 : 2,
            width: 20,
            height: 20,
            borderRadius: 10,
            background: "#fff",
            transition: "left 0.2s",
            boxShadow: "0 1px 3px #0003",
          }}
        />
      </div>
    </div>
  );
}

// ── YC / Unicorn Problem Inspiration (LIVE + classic year picks) ─────────────
function YCInspirationBar({
  onSelect, ycData, loading, error, onRefresh, filters, onFiltersChange,
}) {
  const [hoverIdx, setHoverIdx] = React.useState(-1);
  const [pickedIdx, setPickedIdx] = React.useState(-1);
  const f = filters || { mode: "all", kind: "all", year: "", window: "recent" };
  const isLive = ycData.isLive;
  const years = ycData.years || [];
  // Client-side year filter (guards against backend not filtering)
  const allProblems = ycData.data || [];
  const problems = f.year
    ? allProblems.filter((p) => {
        const py = p.year ? String(p.year) : "";
        const batch = p.batch ? String(p.batch) : "";
        if (py === String(f.year)) return true;
        // Try to extract year from batch string like "W23" → 2023
        const batchYear = batch.replace(/[^0-9]/g, "");
        if (batchYear.length === 2) {
          const full = Number(batchYear) >= 50 ? `19${batchYear}` : `20${batchYear}`;
          if (full === String(f.year)) return true;
        }
        return false;
      })
    : allProblems;

  const chip = (active) => ({
    padding: "4px 10px",
    borderRadius: 999,
    border: `1.5px solid ${active ? "#1B4F72" : "#BFDBFE"}`,
    background: active ? "#1B4F72" : "#E7F0F6",
    color: active ? "#fff" : "#143B56",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  });

  const sel = {
    padding: "4px 8px",
    borderRadius: 6,
    border: "1px solid #BFDBFE",
    background: "#fff",
    color: "#78350F",
    fontSize: 11,
    fontWeight: 600,
  };

  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: 10,
        marginBottom: 14,
        background: "linear-gradient(135deg, #E7F0F6, #E7F0F6)",
        border: "1.5px solid #BFDBFE",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 14 }}>💡</span>
        <span
          style={{ fontSize: 12, color: "#143B56", fontWeight: 700, flex: 1 }}
        >
          YC / Unicorn problems — Live, Classic, or both · years through {new Date().getFullYear()} (present)
        </span>
        {isLive === true && f.mode !== "classic" && (
          <button type="button" className="ps-live-chip" onClick={onRefresh} title="Refresh LIVE web ideas">
            <Badge
              label={`LIVE web · ${ycData.generatedDate || "today"}`}
              color="#059669"
            />
          </button>
        )}
        {f.mode === "classic" && (
          <Badge label={`Classic · ${ycData.classicCount || problems.length}`} color="#143B56" />
        )}
        {isLive === false && f.mode !== "classic" && (
          <Badge label="⚠ offline fallback" color="#DC2626" />
        )}
        <button
          onClick={onRefresh}
          disabled={loading}
          style={{
            padding: "3px 9px",
            borderRadius: 6,
            border: "1.5px solid #1B4F72",
            background: "#E7F0F6",
            color: "#143B56",
            fontSize: 10,
            fontWeight: 700,
            cursor: loading ? "wait" : "pointer",
          }}
        >
          {loading ? "Loading…" : "↻ Refresh"}
        </button>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {[
          { id: "all", label: "Live + Classic" },
          { id: "live", label: "Live" },
          { id: "classic", label: "Classic YC / Unicorn" },
        ].map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onFiltersChange?.({ ...f, mode: m.id })}
            style={chip(f.mode === m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8, alignItems: "center" }}>
        {[
          { id: "all", label: "All kinds" },
          { id: "yc", label: "YC only" },
          { id: "unicorn", label: "Unicorns" },
        ].map((k) => (
          <button
            key={k.id}
            type="button"
            onClick={() => onFiltersChange?.({ ...f, kind: k.id })}
            style={chip(f.kind === k.id)}
          >
            {k.label}
          </button>
        ))}
        <select
          value={f.year || ""}
          onChange={(e) => onFiltersChange?.({ ...f, year: e.target.value })}
          style={sel}
          title="Filter by year"
        >
          <option value="">Any year (through {new Date().getFullYear()})</option>
          {(years.length
            ? years
            : Array.from(
                { length: new Date().getFullYear() - 2004 },
                (_, i) => new Date().getFullYear() - i
              )
          ).map((y) => (
            <option key={y} value={y}>{y}{y === new Date().getFullYear() ? " · present" : ""}</option>
          ))}
        </select>
        {f.mode !== "classic" && (
          <select
            value={f.window || "recent"}
            onChange={(e) => onFiltersChange?.({ ...f, window: e.target.value })}
            style={sel}
            title="Live search window"
          >
            <option value="recent">Live · last few months</option>
            <option value="month">Live · this month</option>
            <option value="quarter">Live · last 3 months</option>
            <option value="year">Live · last 12 months</option>
          </select>
        )}
      </div>

      {error && (
        <div style={{ fontSize: 11, color: C.error, marginBottom: 6 }}>
          {error}
        </div>
      )}

      {!problems.length && !loading && (
        <div style={{ fontSize: 12, color: "#143B56" }}>
          No ideas for this filter — try another year or Live + Classic.
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          maxHeight: 220,
          overflowY: "auto",
        }}
      >
        {problems.map((p, i) => {
          const meta = extractProvenance({}, p);
          const hover = hoverIdx === i;
          const picked = pickedIdx === i;
          return (
          <div
            key={i}
            className={`ps-inspire-row${hover ? " is-hover" : ""}${picked ? " is-picked" : ""}`}
            onMouseEnter={() => setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(-1)}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  color: picked ? "#0F766E" : hover ? "#143B56" : "#78350F",
                  fontWeight: 700,
                }}
              >
                {p.title}
              </div>
              <div style={{ fontSize: 10, color: "#143B56", marginTop: 2, fontWeight: 650 }}>
                {meta.sourceLabel || p.source || "YC / Unicorn"}
                {meta.company ? ` · ${meta.company}` : ""}
                {meta.year ? ` · ${meta.year}` : p.year ? ` · ${p.year}` : ""}
                {meta.batch ? ` · ${meta.batch}` : ""}
                {p.isLive || meta.isLive ? " · LIVE web" : p.kind ? ` · ${String(p.kind).toUpperCase()}` : ""}
              </div>
              {meta.workingOn ? (
                <div style={{ fontSize: 11, color: "#334155", marginTop: 3 }}>{meta.workingOn}</div>
              ) : null}
            </div>
            <button
              className="ps-inspire-use"
              onClick={() => {
                setPickedIdx(i);
                onSelect(p.title);
              }}
            >
              {picked ? "Selected" : hover ? "Use this" : "Use"}
            </button>
          </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Weekly Deliverables Timeline ─────────────────────────────────────────────
// Parses keys like "Week 1 (Empathize & Define)" produced by the backend and
// groups them under their real DT phase, in week order, color-coded to match
// the DT Playbook phase colors. Falls back gracefully for older/plain keys
// like "Week 1" with no phase tag.
function parseWeekKey(key) {
  const m = key.match(/week\s*(\d+)/i);
  const weekNum = m ? parseInt(m[1], 10) : null;
  const phaseMatch = key.match(/\(([^)]+)\)/);
  const phaseLabel = phaseMatch ? phaseMatch[1].trim() : null;
  return { weekNum, phaseLabel };
}

// ── Daily Schedule Component ─────────────────────────────────────────────────
function DailySchedule({ dailySchedule, weeklyDeliverables }) {
  const [activeWeek, setActiveWeek] = useState(1);
  const [activeDay, setActiveDay] = useState(null);

  // Group days by week
  const byWeek = {};
  (dailySchedule || []).forEach((d) => {
    if (!byWeek[d.week]) byWeek[d.week] = [];
    byWeek[d.week].push(d);
  });
  const weeks = Object.keys(byWeek)
    .map(Number)
    .sort((a, b) => a - b);

  // If no daily schedule, fall back to weekly deliverables
  if (!dailySchedule || dailySchedule.length === 0) {
    const entries = Object.entries(weeklyDeliverables || {})
      .map(([key, items]) => {
        const { weekNum, phaseLabel } = parseWeekKey(key);
        const phaseDef = DT_PHASES.find((p) => p.label === phaseLabel) || null;
        return {
          key,
          weekNum: weekNum ?? 999,
          phaseLabel,
          phaseDef,
          items: Array.isArray(items) ? items : [items],
        };
      })
      .sort((a, b) => a.weekNum - b.weekNum);
    return (
      <div style={{ marginBottom: 18 }}>
        <FieldLabel>📦 Weekly Plan</FieldLabel>
        {entries.map((e) => {
          const color = e.phaseDef?.color || C.accent;
          return (
            <div
              key={e.key}
              style={{
                marginBottom: 10,
                borderLeft: `3px solid ${color}`,
                paddingLeft: 10,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 12,
                  color,
                  marginBottom: 4,
                }}
              >
                Week {e.weekNum}
                {e.phaseLabel ? ` — ${e.phaseLabel}` : ""}
              </div>
              {e.items.map((d, j) => (
                <div
                  key={j}
                  style={{
                    fontSize: 12,
                    color: C.subtleText,
                    padding: "2px 0",
                  }}
                >
                  ✓ {d}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  const phaseColors = {
    "Empathize & Define": "#0D9488",
    Plan: "#65A30D",
    Prototype: "#06B6D4",
    Evaluate: "#EC4899",
    "Pitch & BMC": "#8B5CF6",
  };
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const currentDays = byWeek[activeWeek] || [];
  const selected =
    activeDay !== null ? currentDays.find((d) => d.day === activeDay) : null;

  return (
    <div style={{ marginBottom: 18 }}>
      <FieldLabel>📅 Day-by-Day Schedule — SNS DT Playbook</FieldLabel>

      {/* Week tabs */}
      <div
        style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}
      >
        {weeks.map((w) => {
          const phaseOfWeek = byWeek[w]?.[0]?.dtPhase || "";
          const col = phaseColors[phaseOfWeek] || C.accent;
          const active = w === activeWeek;
          return (
            <button
              key={w}
              onClick={() => {
                setActiveWeek(w);
                setActiveDay(null);
              }}
              style={{
                padding: "5px 14px",
                borderRadius: 20,
                border: `2px solid ${col}`,
                background: active ? col : "transparent",
                color: active ? "#fff" : col,
                fontWeight: 700,
                fontSize: 11,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              Week {w}
              <span
                style={{
                  fontWeight: 400,
                  fontSize: 10,
                  marginLeft: 4,
                  opacity: 0.85,
                }}
              >
                {phaseOfWeek}
              </span>
            </button>
          );
        })}
      </div>

      {/* Day cards for the active week */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 8,
          marginBottom: 12,
        }}
      >
        {currentDays.map((d, i) => {
          const col = phaseColors[d.dtPhase] || C.accent;
          const isSelected = activeDay === d.day;
          const hasDSA =
            d.dsaOfTheDay && !d.dsaOfTheDay.toLowerCase().startsWith("none");
          return (
            <div
              key={d.day}
              onClick={() => setActiveDay(isSelected ? null : d.day)}
              style={{
                borderRadius: 10,
                border: `2px solid ${isSelected ? col : C.border}`,
                background: isSelected ? col + "10" : C.card,
                padding: "10px 8px",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 11,
                  color: col,
                  marginBottom: 3,
                }}
              >
                {dayNames[i] || `Day ${d.day}`}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: C.subtleText,
                  lineHeight: 1.4,
                  marginBottom: 4,
                }}
              >
                {d.dtPlaybookActivity?.split("—")[1]?.trim()?.slice(0, 45) ||
                  d.dtPlaybookActivity?.slice(0, 45)}
              </div>
              {hasDSA && (
                <div
                  style={{
                    fontSize: 9,
                    color: col,
                    background: col + "15",
                    borderRadius: 4,
                    padding: "2px 5px",
                    display: "inline-block",
                    fontWeight: 700,
                  }}
                >
                  DSA:{" "}
                  {d.dsaOfTheDay
                    ?.split("—")[0]
                    ?.split(":")[0]
                    ?.trim()
                    ?.slice(0, 18)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Expanded day detail */}
      {selected &&
        (() => {
          const col = phaseColors[selected.dtPhase] || C.accent;
          const hasDSA =
            selected.dsaOfTheDay &&
            !selected.dsaOfTheDay.toLowerCase().startsWith("none");
          return (
            <div
              style={{
                borderRadius: 12,
                border: `2px solid ${col}`,
                background: col + "06",
                padding: 16,
                marginTop: 4,
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 13,
                  color: col,
                  marginBottom: 12,
                }}
              >
                Day {selected.day} — Week {selected.week} — {selected.dtPhase}
              </div>

              {/* 4-column row: Playbook → Learn → DSA → Implement */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    background: C.card,
                    borderRadius: 8,
                    padding: 10,
                    borderLeft: `3px solid ${col}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: col,
                      marginBottom: 4,
                    }}
                  >
                    📖 DT PLAYBOOK ACTIVITY
                  </div>
                  <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                    {selected.dtPlaybookActivity}
                  </div>
                </div>

                <div
                  style={{
                    background: C.card,
                    borderRadius: 8,
                    padding: 10,
                    borderLeft: "3px solid #3B82F6",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#3B82F6",
                      marginBottom: 4,
                    }}
                  >
                    🧠 LEARN TODAY
                  </div>
                  <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                    {selected.learn}
                  </div>
                </div>

                <div
                  style={{
                    background: hasDSA ? "#0D9488" + "08" : C.card,
                    borderRadius: 8,
                    padding: 10,
                    borderLeft: `3px solid ${hasDSA ? "#0D9488" : C.border}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: hasDSA ? "#0D9488" : C.subtleText,
                      marginBottom: 4,
                    }}
                  >
                    💻 DSA OF THE DAY
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: hasDSA ? C.text : C.subtleText,
                      lineHeight: 1.5,
                    }}
                  >
                    {selected.dsaOfTheDay}
                  </div>
                </div>

                <div
                  style={{
                    background: "#10B981" + "08",
                    borderRadius: 8,
                    padding: 10,
                    borderLeft: "3px solid #10B981",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#10B981",
                      marginBottom: 4,
                    }}
                  >
                    🔨 IMPLEMENT
                  </div>
                  <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                    {selected.implement}
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "#8B5CF6" + "08",
                  borderRadius: 8,
                  padding: 10,
                  borderLeft: "3px solid #8B5CF6",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#8B5CF6",
                    marginBottom: 4,
                  }}
                >
                  🪞 END OF DAY REFLECTION
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: C.text,
                    lineHeight: 1.5,
                    fontStyle: "italic",
                  }}
                >
                  "{selected.reflection}"
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}

// ── Statement Card ────────────────────────────────────────────────────────────
function SkillBadge({ level }) {
  const labels = [
    "",
    "Beginner",
    "Novice",
    "Intermediate",
    "Advanced",
    "Expert",
  ];
  const colors = ["", "#DC2626", "#1B4F72", "#0891B2", "#0B5FFF", "#059669"];
  const lvl = Math.min(5, Math.max(1, Number(level) || 3));
  return <Badge label={`${labels[lvl]} (${lvl}/5)`} color={colors[lvl]} />;
}

// ── Team & Student Profiles ──────────────────────────────────────────────────
// Lets faculty manually add real students (with a skill rating) for a given
// team working on this problem statement, then divides the existing
// day-by-day dailySchedule across that team so everyone's tasks stay synced.
function TeamSection({ problemStatementId }) {
  const [teamName, setTeamName] = useState("Team A");
  const [memberCount, setMemberCount] = useState(3); // user says "I have 3 members"
  const [rows, setRows] = useState([]); // [{name, skillLevel, targetCompany}]
  const [saving, setSaving] = useState(false);
  const [teamSchedule, setTeamSchedule] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeWeek, setActiveWeek] = useState(null);
  const [roster, setRoster] = useState([]); // ground-truth members currently saved in the DB for this team
  // How many days the TEAM schedule should cover. This is independent of the
  // problem statement's own duration (2w/4w/8w/12w/16w) — you might link a
  // 40-day problem statement but only want a 10-day team schedule. Leaving
  // this blank uses the full problem-statement length.
  const [teamDays, setTeamDays] = useState(10);
  const [viewMember, setViewMember] = useState(null); // memberId for the per-person full breakdown

  // When member count changes, rebuild the input rows
  useEffect(() => {
    setRows(
      Array.from({ length: memberCount }, (_, i) => ({
        name: "",
        skillLevel: 3,
        targetCompany: "",
      })),
    );
  }, [memberCount]);

  function refreshRoster() {
    if (!problemStatementId || !teamName.trim()) return;
    axios
      .get(`${BASE}/team/members`, {
        params: { problemStatementId, teamName },
        headers: authH(),
      })
      .then((r) => setRoster(r.data?.data || []))
      .catch(() => setRoster([]));
  }

  // Load existing roster + schedule (if already generated) whenever the
  // team changes, so what's on screen always reflects what's in the DB.
  useEffect(() => {
    if (!problemStatementId || !teamName.trim()) return;
    refreshRoster();
    setTeamSchedule(null);
    axios
      .get(`${BASE}/team/schedule`, {
        params: { problemStatementId, teamName },
        headers: authH(),
      })
      .then((r) => {
        if (r.data?.data?.schedule?.length) {
          setTeamSchedule(r.data.data);
          if (r.data.data.members?.[0])
            setViewMember(r.data.data.members[0].id);
        }
      })
      .catch(() => {});
  }, [problemStatementId, teamName]);

  function updateRow(i, field, val) {
    setRows((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)),
    );
  }

  async function handleSaveAndGenerate() {
    const filled = rows.filter((r) => r.name.trim());
    if (!filled.length) {
      setError("Enter at least one name.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccessMsg("");
    try {
      // 1. Replace this team's roster with exactly what's in the form —
      // guarantees the DB always matches what you see on screen (no
      // leftover/duplicate members from earlier saves).
      const bulkRes = await axios.post(
        `${BASE}/team/members/bulk`,
        {
          problemStatementId,
          teamName,
          members: filled.map((r) => ({
            name: r.name.trim(),
            skillLevel: r.skillLevel,
            targetCompany: r.targetCompany?.trim() || "",
          })),
        },
        { headers: authH() },
      );
      const savedMembers = bulkRes.data?.data || [];
      setRoster(savedMembers);

      // 2. Generate the schedule — capped to teamDays so it doesn't silently
      // balloon to the full problem-statement length.
      setGenLoading(true);
      const res = await axios.post(
        `${BASE}/team/generate-schedule`,
        {
          problemStatementId,
          teamName,
          days: teamDays ? Number(teamDays) : undefined,
        },
        { headers: authH() },
      );
      const data = res.data?.data;
      setTeamSchedule(data || null);
      if (data?.schedule?.length) {
        // default to week 1
        setActiveWeek(data.schedule[0]?.week ?? 1);
      }
      if (data?.members?.length) setViewMember(data.members[0].id);
      const names = savedMembers.map((m) => m.name).join(", ");
      setSuccessMsg(
        `Saved ${savedMembers.length} member(s) — ${names} — and generated a ${data?.schedule?.length || teamDays}-day schedule for each.`,
      );
    } catch (e) {
      setError(formatApiError(e, "Something went wrong."));
      refreshRoster(); // re-sync in case the save partially happened before the error
    } finally {
      setSaving(false);
      setGenLoading(false);
    }
  }

  // ── Schedule display ─────────────────────────────────────────────────
  const phaseColors = {
    "Empathize & Define": "#0D9488",
    Plan: "#65A30D",
    Prototype: "#06B6D4",
    Evaluate: "#EC4899",
    "Pitch & BMC": "#8B5CF6",
  };
  const memberColors = ["#0B5FFF", "#059669", "#1B4F72", "#DC2626", "#0891B2"];

  function ScheduleGrid() {
    if (!teamSchedule?.schedule?.length) return null;

    // Collect all member names in order
    const memberNames = [];
    teamSchedule.schedule.forEach((d) => {
      (d.assignments || []).forEach((a) => {
        if (!memberNames.includes(a.name)) memberNames.push(a.name);
      });
    });

    // All unique weeks
    const weeks = [...new Set(teamSchedule.schedule.map((d) => d.week))].sort(
      (a, b) => a - b,
    );
    const currentWeek = activeWeek ?? weeks[0];
    const daysThisWeek = teamSchedule.schedule.filter(
      (d) => d.week === currentWeek,
    );
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri"];

    return (
      <div style={{ marginTop: 16 }}>
        {/* Explicit "who this is for" heading — avoids any ambiguity */}
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: C.text,
            marginBottom: 6,
          }}
        >
          Schedule for {memberNames.length} member
          {memberNames.length === 1 ? "" : "s"}: {memberNames.join(", ")}
        </div>
        {roster.length > 0 && memberNames.length !== roster.length && (
          <div
            style={{
              fontSize: 11,
              color: C.error,
              background: C.errorBg,
              padding: "6px 10px",
              borderRadius: 6,
              marginBottom: 10,
            }}
          >
            ⚠ Roster has {roster.length} saved member(s) but this schedule only
            covers {memberNames.length}. Click "Save & Generate All Schedules"
            again to refresh it.
          </div>
        )}

        {/* Week tabs */}
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          {weeks.map((w) => {
            const phase =
              teamSchedule.schedule.find((d) => d.week === w)?.dtPhase || "";
            const col = phaseColors[phase] || C.accent;
            const active = w === currentWeek;
            return (
              <button
                key={w}
                onClick={() => setActiveWeek(w)}
                style={{
                  padding: "5px 14px",
                  borderRadius: 20,
                  border: `2px solid ${col}`,
                  background: active ? col : "transparent",
                  color: active ? "#fff" : col,
                  fontWeight: 700,
                  fontSize: 11,
                  cursor: "pointer",
                }}
              >
                Week {w}{" "}
                <span style={{ opacity: 0.8, fontWeight: 400 }}>{phase}</span>
              </button>
            );
          })}
        </div>

        {/* Side-by-side grid: rows = days, columns = members */}
        <div
          style={{
            border: `1.5px solid ${C.border}`,
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          {/* Header row: empty corner + one col per member */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `64px repeat(${memberNames.length}, 1fr)`,
              background: C.panelBg,
              borderBottom: `1.5px solid ${C.border}`,
            }}
          >
            <div
              style={{
                padding: "10px 8px",
                borderRight: `1px solid ${C.border}`,
              }}
            />
            {memberNames.map((name, i) => (
              <div
                key={name}
                style={{
                  padding: "10px 12px",
                  borderRight:
                    i < memberNames.length - 1
                      ? `1px solid ${C.border}`
                      : "none",
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: memberColors[i % memberColors.length] + "20",
                    color: memberColors[i % memberColors.length],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 700,
                    marginBottom: 4,
                  }}
                >
                  {name.slice(0, 2).toUpperCase()}
                </div>
                <div style={{ fontWeight: 700, fontSize: 12, color: C.text }}>
                  {name}
                </div>
                <div style={{ fontSize: 10, color: C.muted }}>
                  {teamSchedule.members?.find((m) => m.name === name)
                    ?.skillTag || ""}
                </div>
              </div>
            ))}
          </div>

          {/* Per-member full daily/weekly schedule — the SAME table component
            used for the individual daily plan, so team members get identical
            fidelity: timed rows, checkboxes, color-coded activity types. */}
          <div style={{ marginTop: 20, marginBottom: 20 }}>
            <TeamDayTable
              schedule={teamSchedule.schedule}
              members={teamSchedule.members}
            />
          </div>

          {/* Day rows */}
          {daysThisWeek.map((d, rowIdx) => {
            const col = phaseColors[d.dtPhase] || C.accent;
            const isEven = rowIdx % 2 === 1;
            return (
              <div
                key={d.day}
                style={{
                  display: "grid",
                  gridTemplateColumns: `64px repeat(${memberNames.length}, 1fr)`,
                  background: isEven ? C.bg : C.surface,
                  borderBottom:
                    rowIdx < daysThisWeek.length - 1
                      ? `1px solid ${C.border}`
                      : "none",
                }}
              >
                {/* Day label */}
                <div
                  style={{
                    padding: "10px 8px",
                    textAlign: "center",
                    borderRight: `1px solid ${C.border}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 11, color: col }}>
                    {dayNames[(d.day - 1) % 5] || `D${d.day}`}
                  </div>
                  <div style={{ fontSize: 10, color: C.muted }}>
                    Day {d.day}
                  </div>
                </div>

                {/* One cell per member */}
                {memberNames.map((name, i) => {
                  const assignment = (d.assignments || []).find(
                    (a) => a.name === name,
                  );
                  const mColor = memberColors[i % memberColors.length];
                  return (
                    <div
                      key={name}
                      style={{
                        padding: "10px 12px",
                        borderRight:
                          i < memberNames.length - 1
                            ? `1px solid ${C.border}`
                            : "none",
                      }}
                    >
                      {assignment ? (
                        <>
                          <span
                            style={{
                              display: "inline-block",
                              fontSize: 9,
                              fontWeight: 700,
                              padding: "2px 7px",
                              borderRadius: 99,
                              background:
                                (assignment.role === "Lead Implementer"
                                  ? C.primary
                                  : mColor) + "18",
                              color:
                                assignment.role === "Lead Implementer"
                                  ? C.primary
                                  : mColor,
                              marginBottom: 5,
                            }}
                          >
                            {assignment.role}
                          </span>
                          <div
                            style={{
                              fontSize: 11,
                              color: C.subtleText,
                              lineHeight: 1.4,
                            }}
                          >
                            {assignment.focus}
                          </div>
                          {assignment.placementFocus && (
                            <div
                              style={{
                                fontSize: 10,
                                color: C.primaryDark,
                                lineHeight: 1.4,
                                marginTop: 6,
                                padding: "5px 7px",
                                borderRadius: 6,
                                background: C.primarySoft,
                              }}
                            >
                              🎯 {assignment.placementFocus.type} (
                              {assignment.placementFocus.minutes}m) —{" "}
                              {assignment.placementFocus.task}
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ fontSize: 11, color: C.muted }}>—</div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        marginBottom: 20,
        padding: 16,
        borderRadius: 10,
        background: C.panelBg,
        border: `1.5px solid ${C.border}`,
      }}
    >
      <FieldLabel>👥 Team Schedule</FieldLabel>

      {/* Team name + member count */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 14,
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: "1 1 160px" }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>
            Team name
          </div>
          <TextInput
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="Team A"
          />
        </div>
        <div style={{ flex: "0 0 140px" }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>
            How many members?
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={() => setMemberCount((c) => Math.max(1, c - 1))}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: `1.5px solid ${C.border}`,
                background: C.surface,
                fontSize: 16,
                cursor: "pointer",
                color: C.muted,
              }}
            >
              −
            </button>
            <span
              style={{
                fontWeight: 800,
                fontSize: 20,
                color: C.text,
                minWidth: 20,
                textAlign: "center",
              }}
            >
              {memberCount}
            </span>
            <button
              onClick={() => setMemberCount((c) => Math.min(8, c + 1))}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: `1.5px solid ${C.border}`,
                background: C.surface,
                fontSize: 16,
                cursor: "pointer",
                color: C.muted,
              }}
            >
              +
            </button>
          </div>
        </div>
        <div style={{ flex: "0 0 160px" }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>
            Team schedule length (days)
          </div>
          <input
            type="number"
            min={1}
            value={teamDays}
            onChange={(e) =>
              setTeamDays(
                e.target.value === ""
                  ? ""
                  : Math.max(1, Number(e.target.value)),
              )
            }
            placeholder="e.g. 10"
            style={{
              width: "100%",
              padding: "9px 10px",
              fontSize: 12,
              boxSizing: "border-box",
              border: `1.5px solid ${C.border}`,
              borderRadius: 8,
              color: C.text,
              background: C.surface,
            }}
          />
        </div>
      </div>
      <p
        style={{
          fontSize: 11,
          color: C.muted,
          marginTop: -8,
          marginBottom: 14,
        }}
      >
        This is independent of the problem statement's own project duration —
        set it to exactly the number of days you want each team member's
        schedule to cover.
      </p>

      {/* Ground-truth roster — exactly what's saved in the DB for this team right now */}
      {roster.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            alignItems: "center",
            marginBottom: 14,
            padding: "8px 10px",
            borderRadius: 8,
            background: C.primaryLight,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.primary,
              marginRight: 2,
            }}
          >
            Currently saved ({roster.length}):
          </span>
          {roster.map((m, i) => (
            <span
              key={m._id || i}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                fontWeight: 600,
                color: memberColors[i % memberColors.length],
                background: "#fff",
                padding: "3px 9px",
                borderRadius: 99,
                border: `1px solid ${memberColors[i % memberColors.length]}40`,
              }}
            >
              {m.name}{" "}
              <span style={{ color: C.muted, fontWeight: 400 }}>
                · {m.skillTag}
              </span>
            </span>
          ))}
        </div>
      )}

      {successMsg && (
        <div
          style={{
            fontSize: 12,
            color: C.success,
            background: C.successBg,
            padding: "8px 12px",
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          ✓ {successMsg}
        </div>
      )}

      {/* Auto-generated name rows */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          marginBottom: 14,
        }}
      >
        {rows.map((row, i) => (
          <div
            key={i}
            style={{ display: "flex", gap: 8, alignItems: "center" }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                flexShrink: 0,
                background: memberColors[i % memberColors.length] + "20",
                color: memberColors[i % memberColors.length],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              {i + 1}
            </div>
            <TextInput
              value={row.name}
              onChange={(e) => updateRow(i, "name", e.target.value)}
              placeholder={`Member ${i + 1} name`}
              style={{ flex: 1 }}
            />
            <select
              value={row.skillLevel}
              onChange={(e) =>
                updateRow(i, "skillLevel", Number(e.target.value))
              }
              style={{
                padding: "9px 8px",
                borderRadius: 8,
                border: `1.5px solid ${C.border}`,
                fontSize: 12,
                color: C.text,
              }}
            >
              <option value={1}>1 — Beginner</option>
              <option value={2}>2 — Novice</option>
              <option value={3}>3 — Intermediate</option>
              <option value={4}>4 — Advanced</option>
              <option value={5}>5 — Expert</option>
            </select>
            <TextInput
              value={row.targetCompany}
              onChange={(e) => updateRow(i, "targetCompany", e.target.value)}
              placeholder="Dream company (optional)"
              style={{ flex: 1 }}
            />
          </div>
        ))}
      </div>

      {error && (
        <div
          style={{
            fontSize: 12,
            color: C.error,
            background: C.errorBg,
            padding: "8px 12px",
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <button
        onClick={handleSaveAndGenerate}
        disabled={saving || genLoading}
        style={{
          padding: "10px 18px",
          borderRadius: 8,
          border: "none",
          background: saving || genLoading ? C.muted : C.accent,
          color: "#fff",
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          marginBottom: 4,
        }}
      >
        {saving
          ? "Saving members…"
          : genLoading
            ? "Generating schedules…"
            : "💾 Save & Generate All Schedules"}
      </button>

      <ScheduleGrid />
    </div>
  );
}

function StatementCard({ s, onDelete }) {
  const [open, setOpen] = useState(false);

  // Group weekly deliverables
  const weeklyDeliverables = s.weeklyDeliverables || {};
  const hasWeekly = Object.keys(weeklyDeliverables).length > 0;

  return (
    <div
      className="ps-card"
      style={{
        background: C.surface,
        border: `1.5px solid ${C.border}`,
        borderRadius: 14,
        marginBottom: 12,
        overflow: "hidden",
        boxShadow: open ? `0 4px 24px ${C.primary}14` : "0 1px 4px #0001",
        transition: "box-shadow 0.2s, background 0.15s",
        animation: "fadeIn 0.2s ease both",
      }}
    >
      {/* Header */}
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          padding: "16px 20px",
          cursor: "pointer",
          borderBottom: open ? `1px solid ${C.border}` : "none",
          background: open ? C.panelBg : "transparent",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 6,
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>
                {s.title}
              </span>
              <Badge
                label={s.difficulty}
                color={DIFF_COLORS[s.difficulty] || C.muted}
              />
              <Badge label={s.teamSize} color={C.primary} />
              <Badge label={s.timeline} color="#0891B2" />
              {s.aiIntegration && <Badge label="🤖 GenAI" color={C.ai} />}
            </div>
            <p
              style={{
                margin: "0 0 8px",
                fontSize: 13,
                color: C.muted,
                lineHeight: 1.6,
              }}
            >
              {s.description}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {(s.pillars || []).map((p, i) => (
                <PillarTag key={i} label={p} />
              ))}
            </div>
          </div>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: `1px solid ${C.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: C.muted,
              fontSize: 12,
              flexShrink: 0,
              background: C.bg,
              transition: "all 0.15s",
            }}
          >
            {open ? "▲" : "▼"}
          </div>
        </div>
      </div>

      {/* Expanded body */}
      {open && (
        <div style={{ padding: "20px", animation: "fadeIn 0.15s ease both" }}>
          {/* DT Playbook phases */}
          {s.dtPhases?.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <FieldLabel>📘 DT Playbook Phases</FieldLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {s.dtPhases.map((ph, i) => {
                  const def = DT_PHASES.find(
                    (d) => d.id === ph || d.label === ph,
                  );
                  return (
                    <span
                      key={i}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "5px 11px",
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 600,
                        background: (def?.color || C.accent) + "15",
                        border: `1px solid ${def?.color || C.accent}40`,
                        color: def?.color || C.accent,
                      }}
                    >
                      {def?.emoji} {def?.label || ph}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Subject Integration */}
          {s.subjectMapping && Object.keys(s.subjectMapping).length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <FieldLabel>📚 Subject Integration</FieldLabel>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: 8,
                }}
              >
                {Object.entries(s.subjectMapping).map(([subj, usages], i) => (
                  <div
                    key={i}
                    style={{
                      background: C.primaryLight,
                      borderRadius: 8,
                      padding: "10px 12px",
                      borderLeft: `3px solid ${C.primary}`,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: C.primary,
                        marginBottom: 4,
                      }}
                    >
                      {subj}
                    </div>
                    {(Array.isArray(usages) ? usages : [usages]).map((u, j) => (
                      <div
                        key={j}
                        style={{
                          fontSize: 12,
                          color: C.subtleText,
                          lineHeight: 1.6,
                        }}
                      >
                        ▸ {u}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: hasWeekly ? "1fr" : "1fr 1fr",
              gap: 16,
              marginBottom: 18,
            }}
          >
            {/* Objectives */}
            <div style={{ background: C.bg, borderRadius: 10, padding: 14 }}>
              <FieldLabel>🎯 Objectives</FieldLabel>
              {(s.objectives || []).map((o, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 13,
                    color: C.subtleText,
                    padding: "4px 0",
                    display: "flex",
                    gap: 8,
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    style={{ color: C.primary, fontWeight: 700, flexShrink: 0 }}
                  >
                    {i + 1}.
                  </span>{" "}
                  {o}
                </div>
              ))}
            </div>

            {/* Deliverables (non-weekly fallback) */}
            {!hasWeekly && (
              <div style={{ background: C.bg, borderRadius: 10, padding: 14 }}>
                <FieldLabel>📦 Deliverables</FieldLabel>
                {(s.deliverables || []).map((d, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: 13,
                      color: C.subtleText,
                      padding: "4px 0",
                      display: "flex",
                      gap: 8,
                      lineHeight: 1.5,
                    }}
                  >
                    <span
                      style={{
                        color: C.success,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </span>{" "}
                    {d}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Weekly Deliverables — proper phase-by-phase timeline */}
          <DailySchedule
            dailySchedule={s.dailySchedule}
            weeklyDeliverables={weeklyDeliverables}
          />

          {/* Team & Student Profiles — divide this schedule across a real team */}
          <TeamSection problemStatementId={s._id} />

          {/* SYSTEM DESIGN PHASE */}
          {s.systemDesignPhase?.isRequired && (
            <div
              style={{
                marginBottom: 18,
                padding: "14px 16px",
                borderRadius: 10,
                background: "#F0F9FF",
                border: `1.5px solid #0EA5E9`,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 13,
                  color: "#0369A1",
                  marginBottom: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                📐 System Design Phase (MANDATORY)
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: C.subtleText,
                  lineHeight: 1.6,
                  marginBottom: 8,
                }}
              >
                {s.systemDesignPhase.description ||
                  "Design system architecture before implementation"}
              </div>
              {s.systemDesignPhase.estimatedHours && (
                <div
                  style={{ fontSize: 12, color: "#0369A1", fontWeight: 600 }}
                >
                  ⏱ Estimated: {s.systemDesignPhase.estimatedHours} hours
                </div>
              )}
            </div>
          )}

          {/* LEARNING PREREQUISITES */}
          {s.learningPrerequisites?.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <FieldLabel>
                📚 Learning Prerequisites (Topics to Study)
              </FieldLabel>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: 10,
                }}
              >
                {s.learningPrerequisites.map((prereq, i) => (
                  <div
                    key={i}
                    style={{
                      background: C.primaryLight,
                      borderRadius: 10,
                      padding: "12px 14px",
                      borderLeft: `4px solid ${C.primary}`,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: C.primary,
                        marginBottom: 6,
                      }}
                    >
                      {prereq.topic}
                    </div>
                    {prereq.lectureNotes && (
                      <div
                        style={{
                          fontSize: 12,
                          color: C.subtleText,
                          marginBottom: 6,
                        }}
                      >
                        📖 {prereq.lectureNotes}
                      </div>
                    )}
                    {prereq.resources?.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}
                      >
                        {prereq.resources.map((res, j) => (
                          <div
                            key={j}
                            style={{
                              fontSize: 11,
                              color: C.primary,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            🔗 {res}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* COURSE MODULE MAPPING */}
          {s.courseModules?.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <FieldLabel>🎓 Course Modules Required</FieldLabel>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: 10,
                }}
              >
                {s.courseModules.map((module, i) => {
                  const readinessColor =
                    module.readinessLevel === "Before Start"
                      ? "#DC2626"
                      : module.readinessLevel === "By Week 2"
                        ? "#1B4F72"
                        : "#059669";
                  return (
                    <div
                      key={i}
                      style={{
                        background: readinessColor + "08",
                        borderRadius: 10,
                        padding: "12px 14px",
                        borderLeft: `4px solid ${readinessColor}`,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 12,
                          color: C.text,
                          marginBottom: 6,
                        }}
                      >
                        {module.moduleName}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: C.muted,
                          marginBottom: 6,
                        }}
                      >
                        {module.concepts?.join(" • ")}
                      </div>
                      <div
                        style={{
                          display: "inline-block",
                          padding: "3px 8px",
                          borderRadius: 5,
                          background: readinessColor + "20",
                          color: readinessColor,
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        {module.readinessLevel}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CODING PRACTICE PLATFORM MAPPING */}
          {s.codingPlatforms?.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <FieldLabel>💻 Coding Practice Problems</FieldLabel>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: 10,
                }}
              >
                {s.codingPlatforms.map((platform, i) => {
                  const platformColor =
                    platform.platform === "LeetCode"
                      ? "#2E6BE6"
                      : platform.platform === "HackerRank"
                        ? "#2EC866"
                        : "#9B59B6";
                  const diffColor =
                    platform.difficulty === "Easy"
                      ? "#059669"
                      : platform.difficulty === "Medium"
                        ? "#1B4F72"
                        : "#DC2626";
                  return (
                    <div
                      key={i}
                      style={{
                        background: C.surface,
                        borderRadius: 10,
                        padding: "14px",
                        border: `1.5px solid ${C.border}`,
                        boxShadow: "0 1px 3px #0001",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 10,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: platformColor,
                            background: platformColor + "15",
                            padding: "4px 10px",
                            borderRadius: 6,
                          }}
                        >
                          {platform.platform}
                        </span>
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 11,
                            color: diffColor,
                            background: diffColor + "15",
                            padding: "3px 8px",
                            borderRadius: 4,
                          }}
                        >
                          {platform.difficulty}
                        </span>
                      </div>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: C.text,
                          marginBottom: 8,
                        }}
                      >
                        {platform.problemNumber}
                      </div>
                      {platform.topicsCovered?.length > 0 && (
                        <div
                          style={{
                            fontSize: 11,
                            color: C.muted,
                            marginBottom: 8,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 4,
                          }}
                        >
                          {platform.topicsCovered.map((topic, j) => (
                            <span
                              key={j}
                              style={{
                                background: C.bg,
                                padding: "2px 8px",
                                borderRadius: 4,
                              }}
                            >
                              {topic}
                            </span>
                          ))}
                        </div>
                      )}
                      {platform.relevance && (
                        <div
                          style={{
                            fontSize: 12,
                            color: C.subtleText,
                            lineHeight: 1.5,
                            padding: "10px",
                            background: C.bg,
                            borderRadius: 6,
                            marginTop: 8,
                          }}
                        >
                          <strong style={{ color: C.text }}>
                            Why it matters:
                          </strong>{" "}
                          {platform.relevance}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI Integration Details */}
          {s.aiDetails && (
            <div
              style={{
                marginBottom: 16,
                padding: "12px 14px",
                borderRadius: 10,
                background: C.aiBg,
                border: `1.5px solid ${C.ai}30`,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 12,
                  color: C.ai,
                  marginBottom: 6,
                }}
              >
                🤖 GenAI / AI Integration
              </div>
              <div
                style={{ fontSize: 13, color: C.subtleText, lineHeight: 1.6 }}
              >
                {s.aiDetails}
              </div>
            </div>
          )}

          {s.clubs?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <FieldLabel>🏛 Clubs Involved</FieldLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {s.clubs.map((c, i) => (
                  <Badge
                    key={i}
                    label={`${c.name} (${c.type})`}
                    color={c.type === "technical" ? C.primary : "#EC4899"}
                  />
                ))}
              </div>
            </div>
          )}

          {s.tags?.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                marginBottom: 14,
              }}
            >
              {s.tags.map((t, i) => (
                <Badge key={i} label={`#${t}`} color={C.muted} />
              ))}
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              borderTop: `1px solid ${C.border}`,
              paddingTop: 14,
            }}
          >
            <button
              className="ps-btn-outline"
              onClick={() => onDelete(s._id)}
              style={{
                padding: "7px 16px",
                borderRadius: 8,
                border: `1.5px solid ${C.error}30`,
                background: "#FEF2F2",
                color: C.error,
                fontSize: 12,
                cursor: "pointer",
                fontWeight: 600,
                transition: "all 0.15s",
              }}
            >
              🗑 Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function ProblemStatementGenerator() {
  const [tab, setTab] = useState("generate");
  const [subjects, setSubjects] = useState([{ name: "", syllabusText: "" }]);
  const [clubs, setClubs] = useState([{ name: "", type: "technical" }]);
  // NEW: multi-select weeks (default: 4w)
  const [selectedWeeks, setSelectedWeeks] = useState([4]);
  // NEW: multi-select pillars (default: all)
  const [selectedPillars, setSelectedPillars] = useState(
    FIVE_PILLARS.map((p) => p.id),
  );
  // NEW: DT phases
  const [selectedDTPhases, setSelectedDTPhases] = useState([
    "empathize",
    "plan",
    "prototype",
    "evaluate",
  ]);
  // NEW: AI integration (default ON)
  const [aiIntegration, setAIIntegration] = useState(true);
  // NEW: YC-inspired problem context
  const [problemContext, setProblemContext] = useState("");
  // NEW: full DT playbook reference panel toggle
  const [showPlaybook, setShowPlaybook] = useState(false);
  // NEW: live YC/Unicorn inspiration data
  const [ycData, setYcData] = useState({
    data: [],
    isLive: null,
    generatedDate: null,
    years: [],
    classicCount: 0,
  });
  const [ycFilters, setYcFilters] = useState({
    mode: "all",
    kind: "all",
    year: "",
    window: "recent",
  });
  const [ycLoading, setYcLoading] = useState(false);
  const [ycError, setYcError] = useState("");

  const [count, setCount] = useState(3);
  const [difficulty, setDiff] = useState("intermediate");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.subjects) setSubjects(d.subjects);
        if (d.clubs) setClubs(d.clubs);
        if (d.selectedWeeks) setSelectedWeeks(d.selectedWeeks);
        if (d.selectedPillars) setSelectedPillars(d.selectedPillars);
        if (d.selectedDTPhases) setSelectedDTPhases(d.selectedDTPhases);
        if (d.aiIntegration !== undefined) setAIIntegration(d.aiIntegration);
        if (d.problemContext) setProblemContext(d.problemContext);
        if (d.count) setCount(d.count);
        if (d.difficulty) setDiff(d.difficulty);
      }
    } catch {}
  }, []);

  function saveDraft() {
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({
          subjects,
          clubs,
          selectedWeeks,
          selectedPillars,
          selectedDTPhases,
          aiIntegration,
          problemContext,
          count,
          difficulty,
        }),
      );
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 2000);
    } catch {}
  }

  function clearDraft() {
    localStorage.removeItem(LS_KEY);
    setSubjects([{ name: "", syllabusText: "" }]);
    setClubs([{ name: "", type: "technical" }]);
    setSelectedWeeks([4]);
    setSelectedPillars(FIVE_PILLARS.map((p) => p.id));
    setSelectedDTPhases(["empathize", "plan", "prototype", "evaluate"]);
    setAIIntegration(true);
    setProblemContext("");
    setCount(3);
    setDiff("intermediate");
  }

  useEffect(() => {
    if (tab === "saved") fetchSaved();
  }, [tab]);

  useEffect(() => {
    fetchYCInspiration(false, ycFilters);
  }, [ycFilters.mode, ycFilters.kind, ycFilters.year, ycFilters.window]);

  async function fetchYCInspiration(forceRefresh, filters = ycFilters) {
    setYcLoading(true);
    setYcError("");
    try {
      const q = new URLSearchParams();
      if (forceRefresh) q.set("refresh", "1");
      if (filters.mode) q.set("mode", filters.mode);
      if (filters.kind) q.set("kind", filters.kind);
      if (filters.year) q.set("year", String(filters.year));
      if (filters.window) q.set("window", filters.window);
      const r = await axios.get(
        `${BASE}/problem-statement/yc-inspiration?${q.toString()}`,
        { headers: authH() },
      );
      setYcData({
        data: r.data.data || [],
        isLive: r.data.isLive,
        generatedDate: r.data.generatedDate,
        years: r.data.years || [],
        classicCount: r.data.classicCount || 0,
      });
    } catch (e) {
      setYcError(
        e.response?.data?.error ||
          "Couldn't reach live search — showing fallback ideas if available.",
      );
    }
    setYcLoading(false);
  }

  async function fetchSaved() {
    setSavedLoading(true);
    try {
      const r = await axios.get(`${BASE}/problem-statement`, {
        headers: authH(),
      });
      setSaved(r.data.data || []);
    } catch {
      setSaved([]);
    }
    setSavedLoading(false);
  }

  function addSubject() {
    setSubjects((s) => [...s, { name: "", syllabusText: "" }]);
  }
  function removeSubject(i) {
    setSubjects((s) => s.filter((_, idx) => idx !== i));
  }
  function updateSubject(i, field, val) {
    setSubjects((s) =>
      s.map((x, idx) => (idx === i ? { ...x, [field]: val } : x)),
    );
  }

  function addClub() {
    setClubs((s) => [...s, { name: "", type: "technical" }]);
  }
  function removeClub(i) {
    setClubs((s) => s.filter((_, idx) => idx !== i));
  }
  function updateClub(i, field, val) {
    setClubs((s) =>
      s.map((x, idx) => (idx === i ? { ...x, [field]: val } : x)),
    );
  }

  async function handleGenerate() {
    // Subjects are now OPTIONAL - removed validation
    const validSubjects = subjects.filter((s) => s.name.trim());
    if (!selectedPillars.length) {
      setError("Select at least one pillar.");
      return;
    }
    if (!selectedWeeks.length) {
      setError("Select at least one duration.");
      return;
    }

    setLoading(true);
    setError("");
    setResults([]);
    try {
      const validClubs = clubs.filter((c) => c.name.trim());
      const activePillars = FIVE_PILLARS.filter((p) =>
        selectedPillars.includes(p.id),
      ).map((p) => p.label);
    
  const timelineStr =
        selectedWeeks.length === 1
          ? `${selectedWeeks[0]} weeks`
          : `${totalWeeks} weeks (${selectedWeeks.map((w) => `${w}w`).join(" + ")})`;

      let savedRows = saved;
      try {
        const existing = await axios.get(`${BASE}/problem-statement`, { headers: authH() });
        savedRows = existing.data.data || [];
        setSaved(savedRows);
      } catch {
        /* keep local saved */
      }
      const excludeFingerprints = Array.from(new Set([
        ...loadLocalUsedFingerprints(),
        ...fingerprintsFromRecords(savedRows),
      ]));
      const r = await axios.post(
        `${BASE}/problem-statement/generate`,
        {
          // subjects is now optional
          courseModules: validSubjects.length
            ? validSubjects.map((s) => ({
                moduleName: s.name,
                concepts: s.syllabusText
                  ? s.syllabusText.split(",").map((x) => x.trim())
                  : [],
              }))
            : [],
          clubs: validClubs,
          count,
          difficulty,
          timeline: timelineStr,
          selectedWeeks,
          pillars: activePillars,
          dtPhases: selectedDTPhases,
          aiIntegration,
          problemContext: problemContext.trim(),
          uniqueOnly: true,
          excludeAssigned: true,
          excludeFingerprints,
          detailed: false,
          minWords: 60,
        },
        { headers: authH() },
      );
      const next = (r.data.data || []).filter(
        (s) => !isStatementTaken(`${s.title || ""} ${s.description || ""}`, excludeFingerprints)
      );
      next.forEach((s) => claimLocalFingerprint(`${s.title || ""} ${s.description || ""}`, {
        company: s.company,
        year: s.year,
        sourceKind: s.sourceKind,
      }));
      setResults(next);
      if (!next.length) {
        setError("Every matching statement is already assigned. Change filters and generate again.");
      }
    } catch (e) {
      setError(formatApiError(e, "Request failed"));
    }
    setLoading(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this problem statement?")) return;
    try {
      await axios.delete(`${BASE}/problem-statement/${id}`, {
        headers: authH(),
      });
      setSaved((s) => s.filter((x) => x._id !== id));
      setResults((r) => r.filter((x) => x._id !== id));
    } catch {}
  }

  function downloadAll(list) {
    const lines = [];
    list.forEach((s, i) => {
      lines.push(
        `\n${"=".repeat(60)}\nProblem Statement ${i + 1}: ${s.title}\n${"=".repeat(60)}`,
      );
      lines.push(
        `Difficulty: ${s.difficulty}  |  Team: ${s.teamSize}  |  Timeline: ${s.timeline}`,
      );
      if (s.aiIntegration) lines.push("GenAI Integration: YES");
      lines.push(`\nDescription:\n${s.description}`);
      lines.push(`\nSubjects: ${s.subjects?.join(", ")}`);
      lines.push(`\nPillars: ${s.pillars?.join(", ")}`);
      lines.push(`\nDT Phases: ${s.dtPhases?.join(", ")}`);
      lines.push(
        `\nObjectives:\n${(s.objectives || []).map((o, j) => `${j + 1}. ${o}`).join("\n")}`,
      );
      const wkDel = s.weeklyDeliverables || {};
      if (Object.keys(wkDel).length) {
        lines.push("\nWeekly Deliverables:");
        Object.entries(wkDel).forEach(([wk, items]) => {
          lines.push(`  ${wk}:`);
          (Array.isArray(items) ? items : [items]).forEach((d) =>
            lines.push(`    ✓ ${d}`),
          );
        });
      } else {
        lines.push(
          `\nDeliverables:\n${(s.deliverables || []).map((d) => "✓ " + d).join("\n")}`,
        );
      }
      if (s.aiDetails) lines.push(`\nAI/GenAI Details:\n${s.aiDetails}`);
    });
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "ProblemStatements.txt";
    a.click();
  }


  function downloadAllPdf(list) {
    const htmlCards = (list || []).map((s, i) => {
      const objectives = (s.objectives || []).map((o, j) => `<li>${j + 1}. ${String(o || "")}</li>`).join("");
      const wkDel = s.weeklyDeliverables || {};
      const wkRows = Object.keys(wkDel).map((wk) => {
        const items = Array.isArray(wkDel[wk]) ? wkDel[wk] : [wkDel[wk]];
        return `<div><strong>${wk}:</strong> ${items.map((d) => `✓ ${String(d || "")}`).join("; ")}</div>`;
      }).join("");
      return `
        <section style="margin-bottom:18px; padding:14px; border:1px solid #dbe2ea; border-radius:10px;">
          <h2 style="margin:0 0 8px; font-size:16px;">${i + 1}. ${String(s.title || "Problem Statement")}</h2>
          <p style="margin:0 0 8px; font-size:12px; color:#475569;">Difficulty: ${String(s.difficulty || "-")} | Team: ${String(s.teamSize || "-")} | Timeline: ${String(s.timeline || "-")}</p>
          <p style="margin:0 0 10px; white-space:pre-wrap; line-height:1.45;">${String(s.description || "")}</p>
          <p style="margin:0 0 6px;"><strong>Subjects:</strong> ${(s.subjects || []).join(", ")}</p>
          <p style="margin:0 0 6px;"><strong>Pillars:</strong> ${(s.pillars || []).join(", ")}</p>
          <p style="margin:0 0 6px;"><strong>DT Phases:</strong> ${(s.dtPhases || []).join(", ")}</p>
          <div style="margin:0 0 6px;"><strong>Objectives:</strong><ol style="margin:6px 0 0 18px;">${objectives}</ol></div>
          ${wkRows ? `<div style="margin:0 0 6px;"><strong>Weekly Deliverables:</strong>${wkRows}</div>` : ""}
          ${s.aiDetails ? `<div style="margin-top:8px;"><strong>AI/GenAI Details:</strong><p style="margin:6px 0 0; white-space:pre-wrap;">${String(s.aiDetails)}</p></div>` : ""}
        </section>
      `;
    }).join("");

    const w = window.open("", "_blank", "width=1024,height=768");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8" /><title>Problem Statements</title></head><body style="font-family:Arial,sans-serif; padding:20px; color:#0f172a;"><h1 style="margin:0 0 14px;">Problem Statements</h1>${htmlCards}<script>window.onload=function(){window.print();};<\/script></body></html>`);
    w.document.close();
  }
  const totalWeeks = selectedWeeks.reduce((a, b) => a + b, 0);

  return (
    <>
      <style>{globalStyles}</style>
      <div
        style={{
          maxWidth: 1160,
          margin: "0 auto",
          padding: "32px 20px 60px",
          fontFamily: FONT.family,
          background: C.bg,
          minHeight: "100vh",
        }}
      >
        <PageHeader
          title="Problem Statement"
          subtitle="DT Playbook–aligned · 5 Pillars · YC/Unicorn inspired · GenAI integrated"
          icon="🧩"
          style={{ marginBottom: 22 }}
        />

        {/* Tab Bar */}
        <div
          style={{
            display: "flex",
            gap: 2,
            marginBottom: 24,
            background: C.surface,
            padding: 4,
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            width: "fit-content",
            boxShadow: "0 1px 3px #0001",
          }}
        >
          {[
            ["generate", "⚡ Generate"],
            ["saved", "🗂 All Statements"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                padding: "8px 22px",
                borderRadius: 7,
                border: "none",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 13,
                background:
                  tab === key
                    ? `linear-gradient(135deg, ${C.accent}, ${C.primary})`
                    : "transparent",
                color: tab === key ? "#fff" : C.muted,
                transition: "all 0.15s",
                boxShadow: tab === key ? `0 2px 8px ${C.accent}30` : "none",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* GENERATE TAB */}
        {tab === "generate" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <div
              style={{
                background: C.surface,
                borderRadius: 16,
                border: `1.5px solid ${C.border}`,
                boxShadow: "0 2px 12px #0002",
              }}
            >
              {/* Panel header */}
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: `1px solid ${C.border}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderRadius: "16px 16px 0 0",
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  Configure
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    className="ps-btn-outline"
                    onClick={saveDraft}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 7,
                      border: `1.5px solid ${draftSaved ? C.success : C.border}`,
                      background: draftSaved ? C.successBg : "transparent",
                      color: draftSaved ? C.success : C.muted,
                      fontSize: 11,
                      cursor: "pointer",
                      fontWeight: 600,
                      transition: "all 0.15s",
                    }}
                  >
                    {draftSaved ? "✓ Saved!" : "💾 Draft"}
                  </button>
                  <button
                    className="ps-btn-outline"
                    onClick={clearDraft}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 7,
                      border: `1.5px solid ${C.border}`,
                      background: "transparent",
                      color: C.muted,
                      fontSize: 11,
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    ↺ Clear
                  </button>
                </div>
              </div>

              <div style={{ padding: "24px 24px 20px" }}>
                {/* YC Inspiration Bar */}
                <YCInspirationBar
                  onSelect={(v) => setProblemContext(v)}
                  ycData={ycData}
                  loading={ycLoading}
                  error={ycError}
                  onRefresh={() => fetchYCInspiration(true)}
                  filters={ycFilters}
                  onFiltersChange={setYcFilters}
                />

                {/* Problem Context */}
                <div style={{ marginBottom: 20 }}>
                  <FieldLabel>
                    Problem Context / YC-style Idea{" "}
                    <span
                      style={{
                        fontWeight: 400,
                        textTransform: "none",
                        fontSize: 10,
                      }}
                    >
                      (optional — seeds the AI)
                    </span>
                  </FieldLabel>
                  <TextInput
                    value={problemContext}
                    onChange={(e) => setProblemContext(e.target.value)}
                    placeholder="e.g. AI-powered mental health companion for college students"
                  />
                </div>

                {/* AI Toggle */}
                <div style={{ marginBottom: 20 }}>
                  <AIIntegrationToggle
                    value={aiIntegration}
                    onChange={setAIIntegration}
                  />
                </div>

                {/* 3-col grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 24,
                  }}
                >
                  {/* Col 1: Subjects + Clubs */}
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 10,
                      }}
                    >
                      <FieldLabel>
                        📚 Course Modules{" "}
                        <span
                          style={{
                            fontWeight: 400,
                            textTransform: "none",
                            fontSize: 10,
                            color: C.muted,
                          }}
                        >
                          (optional — for learning connectivity)
                        </span>
                      </FieldLabel>
                      <button
                        className="ps-btn-outline"
                        onClick={addSubject}
                        style={{
                          padding: "4px 12px",
                          borderRadius: 6,
                          border: `1.5px solid ${C.primary}`,
                          background: C.primaryLight,
                          color: C.primary,
                          fontSize: 12,
                          cursor: "pointer",
                          fontWeight: 600,
                          transition: "all 0.15s",
                        }}
                      >
                        + Add
                      </button>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {subjects.map((s, i) => (
                        <div
                          key={i}
                          className="ps-subject-card"
                          style={{
                            background: C.bg,
                            borderRadius: 10,
                            padding: "12px 14px",
                            border: `1.5px solid ${C.border}`,
                            transition: "border-color 0.15s",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              alignItems: "center",
                              marginBottom: 8,
                            }}
                          >
                            <span
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: 6,
                                background: C.primaryLight,
                                color: C.primary,
                                fontSize: 11,
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {i + 1}
                            </span>
                            <TextInput
                              value={s.name}
                              onChange={(e) =>
                                updateSubject(i, "name", e.target.value)
                              }
                              placeholder="Subject name"
                              style={{ flex: 1 }}
                            />
                            {subjects.length > 1 && (
                              <button
                                onClick={() => removeSubject(i)}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  border: `1px solid ${C.error}30`,
                                  background: "#FEF2F2",
                                  color: C.error,
                                  fontSize: 13,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          <textarea
                            value={s.syllabusText}
                            onChange={(e) =>
                              updateSubject(i, "syllabusText", e.target.value)
                            }
                            placeholder="Paste syllabus content (optional)"
                            rows={2}
                            style={{
                              width: "100%",
                              padding: "8px 10px",
                              border: `1.5px solid ${C.border}`,
                              borderRadius: 7,
                              fontSize: 12,
                              resize: "vertical",
                              background: C.surface,
                              color: C.text,
                              lineHeight: 1.5,
                              transition:
                                "border-color 0.15s, box-shadow 0.15s",
                            }}
                          />
                        </div>
                      ))}
                    </div>

                    <Divider />

                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 10,
                        }}
                      >
                        <FieldLabel>
                          Clubs{" "}
                          <span
                            style={{
                              fontWeight: 400,
                              textTransform: "none",
                              fontSize: 10,
                            }}
                          >
                            (optional)
                          </span>
                        </FieldLabel>
                        <button
                          className="ps-btn-outline"
                          onClick={addClub}
                          style={{
                            padding: "4px 12px",
                            borderRadius: 6,
                            border: `1.5px solid ${C.accent}`,
                            background: C.accent + "10",
                            color: C.accent,
                            fontSize: 12,
                            cursor: "pointer",
                            fontWeight: 600,
                            transition: "all 0.15s",
                          }}
                        >
                          + Add
                        </button>
                      </div>
                      {clubs.map((c, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            gap: 6,
                            marginBottom: 6,
                            alignItems: "center",
                          }}
                        >
                          <TextInput
                            value={c.name}
                            onChange={(e) =>
                              updateClub(i, "name", e.target.value)
                            }
                            placeholder="Club name"
                            style={{ flex: 1 }}
                          />
                          <select
                            value={c.type}
                            onChange={(e) =>
                              updateClub(i, "type", e.target.value)
                            }
                            style={{
                              padding: "9px 8px",
                              border: `1.5px solid ${C.border}`,
                              borderRadius: 8,
                              fontSize: 12,
                              background: C.surface,
                              color: C.text,
                              cursor: "pointer",
                            }}
                          >
                            <option value="technical">Technical</option>
                            <option value="non-technical">Non-Technical</option>
                          </select>
                          <button
                            onClick={() => removeClub(i)}
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 7,
                              border: `1px solid ${C.border}`,
                              background: C.bg,
                              color: C.muted,
                              fontSize: 13,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Col 2: Pillars + DT Phases */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 20,
                    }}
                  >
                    <PillarMultiSelect
                      selected={selectedPillars}
                      onChange={setSelectedPillars}
                    />
                    <Divider />
                    <DTPhaseSelector
                      selected={selectedDTPhases}
                      onChange={setSelectedDTPhases}
                      showPlaybook={showPlaybook}
                      onTogglePlaybook={() => setShowPlaybook((v) => !v)}
                    />
                  </div>

                  {/* Col 3: Weeks + Count + Difficulty + Generate */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 20,
                    }}
                  >
                    <WeekMultiSelect
                      selected={selectedWeeks}
                      onChange={setSelectedWeeks}
                      dtPhases={selectedDTPhases}
                    />

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 14,
                      }}
                    >
                      <div>
                        <FieldLabel>Statements</FieldLabel>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <button
                            onClick={() => setCount((c) => Math.max(1, c - 1))}
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 8,
                              border: `1.5px solid ${C.border}`,
                              background: C.surface,
                              fontSize: 18,
                              cursor: "pointer",
                              color: C.muted,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            −
                          </button>
                          <span
                            style={{
                              fontWeight: 900,
                              fontSize: 24,
                              minWidth: 28,
                              textAlign: "center",
                              color: C.text,
                            }}
                          >
                            {count}
                          </span>
                          <button
                            onClick={() => setCount((c) => Math.min(5, c + 1))}
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 8,
                              border: `1.5px solid ${C.border}`,
                              background: C.surface,
                              fontSize: 18,
                              cursor: "pointer",
                              color: C.muted,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            +
                          </button>
                        </div>
                        <div
                          style={{ fontSize: 10, color: C.muted, marginTop: 4 }}
                        >
                          1 to 5
                        </div>
                      </div>
                      <div>
                        <FieldLabel>Difficulty</FieldLabel>
                        <select
                          value={difficulty}
                          onChange={(e) => setDiff(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "9px 10px",
                            border: `1.5px solid ${C.border}`,
                            borderRadius: 8,
                            fontSize: 13,
                            background: C.surface,
                            color: C.text,
                            cursor: "pointer",
                          }}
                        >
                          <option value="beginner">🟢 Beginner</option>
                          <option value="intermediate">🟡 Intermediate</option>
                          <option value="advanced">🔴 Advanced</option>
                        </select>
                      </div>
                    </div>

                    {/* Summary strip */}
                    <div
                      style={{
                        padding: "10px 12px",
                        borderRadius: 9,
                        fontSize: 12,
                        lineHeight: 1.7,
                        background: C.panelBg,
                        border: `1px solid ${C.border}`,
                      }}
                    >
                      <div>
                        <b>Pillars:</b>{" "}
                        {selectedPillars.length
                          ? `${selectedPillars.length} selected`
                          : "⚠ none"}
                      </div>
                      <div>
                        <b>Duration:</b>{" "}
                        {selectedWeeks.length
                          ? `${totalWeeks}w total`
                          : "⚠ none"}
                      </div>
                      <div>
                        <b>DT Phases:</b>{" "}
                        {selectedDTPhases.length
                          ? selectedDTPhases.length
                          : "none"}
                      </div>
                      <div>
                        <b>GenAI:</b> {aiIntegration ? "✅ ON" : "Off"}
                      </div>
                    </div>

                    {error && (
                      <div
                        style={{
                          background: C.errorBg,
                          color: C.error,
                          padding: "10px 14px",
                          borderRadius: 9,
                          fontSize: 13,
                          borderLeft: `3px solid ${C.error}`,
                          display: "flex",
                          gap: 8,
                          alignItems: "flex-start",
                        }}
                      >
                        <span>⚠</span>
                        <span>{error}</span>
                      </div>
                    )}

                    <button
                      className="ps-btn-primary"
                      onClick={handleGenerate}
                      disabled={loading}
                      style={{
                        width: "100%",
                        padding: "13px 0",
                        background: loading
                          ? C.muted
                          : `linear-gradient(135deg, ${C.accent}, ${C.primary})`,
                        color: "#fff",
                        border: "none",
                        borderRadius: 10,
                        fontWeight: 800,
                        fontSize: 14,
                        cursor: loading ? "not-allowed" : "pointer",
                        letterSpacing: "0.02em",
                        boxShadow: loading
                          ? "none"
                          : `0 4px 14px ${C.accent}30`,
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      {loading ? (
                        <>
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              border: "2px solid #ffffff60",
                              borderTopColor: "#fff",
                              borderRadius: "50%",
                              display: "inline-block",
                              animation: "spin 0.7s linear infinite",
                            }}
                          />
                          Generating…
                        </>
                      ) : (
                        `🧩 Generate ${count} Statement${count > 1 ? "s" : ""}`
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div>
              {!results.length && !loading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "80px 20px",
                    color: C.muted,
                    background: C.surface,
                    borderRadius: 16,
                    border: `2px dashed ${C.border}`,
                  }}
                >
                  <div style={{ fontSize: 56, marginBottom: 14 }}>🧩</div>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 17,
                      marginBottom: 6,
                      color: C.text,
                    }}
                  >
                    No statements yet
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      maxWidth: 380,
                      margin: "0 auto",
                      lineHeight: 1.6,
                    }}
                  >
                    Select pillars & duration, optionally add course modules for
                    learning connectivity, then click Generate
                  </div>
                </div>
              )}

              {loading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "72px 20px",
                    background: C.surface,
                    borderRadius: 16,
                    border: `1.5px solid ${C.border}`,
                  }}
                >
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: "50%",
                      border: `3px solid ${C.border}`,
                      borderTopColor: C.accent,
                      margin: "0 auto 20px",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 16,
                      color: C.text,
                      marginBottom: 6,
                    }}
                  >
                    Generating problem statements…
                  </div>
                  <div
                    style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}
                  >
                    {count} statement{count > 1 ? "s" : ""} · {totalWeeks} weeks
                    · {selectedPillars.length} pillars · Usually 20–40 seconds
                  </div>
                  <div
                    style={{
                      maxWidth: 280,
                      margin: "0 auto",
                      height: 4,
                      background: C.border,
                      borderRadius: 99,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: "50%",
                        background: `linear-gradient(90deg, ${C.accent}, ${C.primary})`,
                        borderRadius: 99,
                        animation: "shimmer 1.6s ease-in-out infinite",
                      }}
                    />
                  </div>
                </div>
              )}

              {results.length > 0 && (
                <div style={{ animation: "fadeIn 0.2s ease both" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 16,
                    }}
                  >
                    <div>
                      <span
                        style={{ fontWeight: 800, fontSize: 16, color: C.text }}
                      >
                        {results.length} Statement
                        {results.length > 1 ? "s" : ""} Generated
                      </span>
                      <span
                        style={{ fontSize: 12, color: C.muted, marginLeft: 8 }}
                      >
                        {totalWeeks}w · {selectedPillars.length} pillars ·{" "}
                        {aiIntegration ? "GenAI ON" : ""}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        className="ps-btn-outline"
                        onClick={() => downloadAll(results)}
                        style={{
                          padding: "8px 16px",
                          borderRadius: 8,
                          border: `1.5px solid ${C.border}`,
                          background: C.surface,
                          color: C.primary,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        ⬇ Download TXT
                      </button>
                      <button
                        className="ps-btn-outline"
                        onClick={() => downloadAllPdf(results)}
                        style={{
                          padding: "8px 16px",
                          borderRadius: 8,
                          border: `1.5px solid ${C.border}`,
                          background: C.surface,
                          color: C.primary,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        ⬇ Download PDF
                      </button>
                    </div>
                  </div>
                  {results.map((s, i) => (
                    <StatementCard
                      key={s._id || i}
                      s={s}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SAVED TAB */}
        {tab === "saved" && (
          <div>
            {savedLoading && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  padding: 48,
                  color: C.muted,
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    border: `2px solid ${C.border}`,
                    borderTopColor: C.accent,
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                Loading statements…
              </div>
            )}
            {!savedLoading && !saved.length && (
              <div
                style={{
                  textAlign: "center",
                  padding: 72,
                  color: C.muted,
                  background: C.surface,
                  borderRadius: 14,
                  border: `2px dashed ${C.border}`,
                }}
              >
                <div style={{ fontSize: 44, marginBottom: 10 }}>📭</div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  No saved statements yet
                </div>
                <div style={{ fontSize: 13, marginTop: 6 }}>
                  Generate statements and they'll appear here
                </div>
              </div>
            )}
            {saved.length > 0 && (
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 16,
                  }}
                >
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      className="ps-btn-outline"
                      onClick={() => downloadAll(saved)}
                      style={{
                        padding: "8px 16px",
                        borderRadius: 8,
                        border: `1.5px solid ${C.border}`,
                        background: C.surface,
                        color: C.primary,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      ⬇ Download TXT
                    </button>
                    <button
                      className="ps-btn-outline"
                      onClick={() => downloadAllPdf(saved)}
                      style={{
                        padding: "8px 16px",
                        borderRadius: 8,
                        border: `1.5px solid ${C.border}`,
                        background: C.surface,
                        color: C.primary,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      ⬇ Download PDF
                    </button>
                  </div>
                </div>
                {saved.map((s, i) => (
                  <StatementCard
                    key={s._id || i}
                    s={s}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}



